import React, { useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  ModuleRegistry,
  ClientSideRowModelModule,
  ValidationModule,
  PaginationModule,
  RowAutoHeightModule,
  TextFilterModule,
} from 'ag-grid-community';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Popover,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Comment as CommentIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ValidationModule,
  PaginationModule,
  RowAutoHeightModule,
  TextFilterModule,
]);

export const CV_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'hired', label: 'Hired' },
  { value: 'archived', label: 'Archived' },
] as const;

export type CvStatus = (typeof CV_STATUS_OPTIONS)[number]['value'];

const VISIBLE_TAG_COUNT = 2;

/** Red → orange → yellow → green stops for recruiter rating. */
const RATING_COLOR_STOPS: Array<{ t: number; rgb: [number, number, number] }> = [
  { t: 0, rgb: [229, 57, 53] },
  { t: 0.33, rgb: [251, 140, 0] },
  { t: 0.66, rgb: [253, 216, 53] },
  { t: 1, rgb: [67, 160, 71] },
];

const RATING_GRADIENT =
  'linear-gradient(90deg, #e53935 0%, #fb8c00 33%, #fdd835 66%, #43a047 100%)';

export function ratingToCssColor(rating: number | null | undefined): string | null {
  if (rating == null || Number.isNaN(rating)) return null;
  const t = Math.min(1, Math.max(0, rating));
  let left = RATING_COLOR_STOPS[0];
  let right = RATING_COLOR_STOPS[RATING_COLOR_STOPS.length - 1];
  for (let i = 0; i < RATING_COLOR_STOPS.length - 1; i += 1) {
    if (t >= RATING_COLOR_STOPS[i].t && t <= RATING_COLOR_STOPS[i + 1].t) {
      left = RATING_COLOR_STOPS[i];
      right = RATING_COLOR_STOPS[i + 1];
      break;
    }
  }
  const span = right.t - left.t || 1;
  const local = (t - left.t) / span;
  const rgb = left.rgb.map((channel, index) =>
    Math.round(channel + (right.rgb[index] - channel) * local)
  ) as [number, number, number];
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

interface CVBankRow {
  id: number;
  candidate_name?: string | null;
  candidate_location?: string | null;
  candidate_job_title?: string | null;
  candidate_email?: string | null;
  candidate_experience?: number | null;
  candidate_skills?: string[] | null;
  candidate_profile_id?: number | null;
  status: string;
  rating?: number | null;
  tags?: string[] | null;
  custom_tags?: string[] | null;
  recruiter_notes?: string | null;
  file_size: number;
  original_filename: string;
}

interface CVBankTableAgGridProps {
  cvs: CVBankRow[];
  onStatusChange: (cvId: number, status: string, customTags: string[], recruiterNotes: string) => void;
  onRatingChange: (cvId: number, rating: number | null) => void;
  onDownload: (cvId: number, filename: string) => void;
  onComments: (cv: CVBankRow) => void;
  onEditProfile: (profileId: number) => void;
  onDelete: (cvId: number) => void;
  profileDialogLoading?: boolean;
}

const getStatusSelectColor = (status: string): string => {
  switch (status) {
    case 'interviewed':
      return 'info.main';
    case 'rejected':
      return 'error.main';
    case 'hired':
      return 'success.main';
    case 'archived':
      return 'text.secondary';
    default:
      return 'text.primary';
  }
};

const TagsCell: React.FC<{ tags: string[] }> = ({ tags }) => {
  if (!tags.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const visible = tags.slice(0, VISIBLE_TAG_COUNT);
  const overflow = tags.slice(VISIBLE_TAG_COUNT);
  const overflowCount = overflow.length;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', py: 0.5 }}>
      {visible.map((tag) => (
        <Chip key={tag} label={tag} size="small" variant="outlined" />
      ))}
      {overflowCount > 0 && (
        <Tooltip
          title={
            <Box sx={{ maxWidth: 320, maxHeight: 240, overflow: 'auto', py: 0.5 }}>
              {tags.map((tag) => (
                <Typography key={tag} variant="caption" display="block" sx={{ lineHeight: 1.6 }}>
                  {tag}
                </Typography>
              ))}
            </Box>
          }
          arrow
          placement="top"
        >
          <Chip
            label={`+${overflowCount}`}
            size="small"
            variant="outlined"
            sx={{ cursor: 'help' }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

const RatingColorSquare: React.FC<{
  rating: number | null | undefined;
  onChange: (rating: number | null) => void;
}> = ({ rating, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<number | null>(rating ?? null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const draftRef = useRef<number | null>(rating ?? null);
  const color = ratingToCssColor(draft);

  React.useEffect(() => {
    if (!draggingRef.current) {
      setDraft(rating ?? null);
      draftRef.current = rating ?? null;
    }
  }, [rating]);

  const pickFromClientX = (clientX: number) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const next = Math.round(Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) * 100) / 100;
    draftRef.current = next;
    setDraft(next);
  };

  const commitDraft = () => {
    onChange(draftRef.current);
  };

  return (
    <>
      <Tooltip title={draft == null ? 'Set rating color' : 'Change rating color'} placement="top">
        <Box
          component="button"
          type="button"
          aria-label="Candidate rating color"
          onClick={(e) => {
            e.stopPropagation();
            setDraft(rating ?? null);
            draftRef.current = rating ?? null;
            setAnchorEl(e.currentTarget);
          }}
          sx={{
            width: 14,
            height: 14,
            minWidth: 14,
            p: 0,
            borderRadius: 0.5,
            border: color ? '1px solid rgba(0,0,0,0.2)' : '1px dashed',
            borderColor: color ? 'rgba(0,0,0,0.2)' : 'divider',
            bgcolor: color || 'transparent',
            cursor: 'pointer',
            flexShrink: 0,
            mt: '2px',
            display: 'block',
            '&:hover': {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 1,
            },
          }}
        />
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          if (draggingRef.current) return;
          commitDraft();
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 1.5, width: 220 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Red → green rating
          </Typography>
          <Box
            ref={trackRef}
            onMouseDown={(e) => {
              e.preventDefault();
              draggingRef.current = true;
              pickFromClientX(e.clientX);
              const onMove = (ev: MouseEvent) => {
                if (!draggingRef.current) return;
                pickFromClientX(ev.clientX);
              };
              const onUp = () => {
                draggingRef.current = false;
                commitDraft();
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
              };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            }}
            sx={{
              position: 'relative',
              height: 18,
              borderRadius: 1,
              background: RATING_GRADIENT,
              cursor: 'ew-resize',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {draft != null && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -3,
                  left: `calc(${draft * 100}% - 6px)`,
                  width: 12,
                  height: 24,
                  borderRadius: 0.5,
                  bgcolor: '#fff',
                  border: '2px solid',
                  borderColor: 'text.primary',
                  boxShadow: 1,
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
            <Typography variant="caption" color="error.main">Poor</Typography>
            <Typography variant="caption" color="success.main">Strong</Typography>
          </Box>
          <Button
            size="small"
            fullWidth
            sx={{ mt: 1 }}
            onClick={() => {
              draftRef.current = null;
              setDraft(null);
              onChange(null);
              setAnchorEl(null);
            }}
          >
            Clear
          </Button>
        </Box>
      </Popover>
    </>
  );
};

const CVBankTableAgGrid: React.FC<CVBankTableAgGridProps> = ({
  cvs,
  onStatusChange,
  onRatingChange,
  onDownload,
  onComments,
  onEditProfile,
  onDelete,
  profileDialogLoading = false,
}) => {
  const theme = useTheme();

  const columnDefs = useMemo<ColDef<CVBankRow>[]>(
    () => [
      {
        colId: 'candidate',
        headerName: 'Candidate',
        minWidth: 200,
        flex: 1.4,
        valueGetter: (params) => params.data?.candidate_name || 'No profile',
        cellRenderer: (params: { data?: CVBankRow }) => {
          const cv = params.data;
          if (!cv) return null;
          return (
            <Box sx={{ py: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <RatingColorSquare
                rating={cv.rating}
                onChange={(nextRating) => onRatingChange(cv.id, nextRating)}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                  {cv.candidate_name || 'No profile'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cv.candidate_location || 'Location not set'}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        colId: 'job_title',
        headerName: 'Job Title',
        field: 'candidate_job_title',
        minWidth: 140,
        flex: 1.1,
        valueFormatter: (params) => params.value || '-',
      },
      {
        colId: 'contact',
        headerName: 'Contact',
        field: 'candidate_email',
        minWidth: 180,
        flex: 1.2,
        cellRenderer: (params: { value?: string | null }) => {
          const email = params.value?.trim();
          if (!email) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Link
              href={`mailto:${email}`}
              underline="hover"
              onClick={(e) => e.stopPropagation()}
              sx={{ fontSize: '0.875rem' }}
            >
              {email}
            </Link>
          );
        },
      },
      {
        colId: 'experience',
        headerName: 'Experience',
        minWidth: 140,
        flex: 1,
        valueGetter: (params) => params.data?.candidate_experience ?? null,
        cellRenderer: (params: { data?: CVBankRow }) => {
          const cv = params.data;
          if (!cv) return null;
          const skills = cv.candidate_skills || [];
          const visibleSkills = skills.slice(0, VISIBLE_TAG_COUNT);
          const overflowSkills = skills.slice(VISIBLE_TAG_COUNT);

          return (
            <Box sx={{ py: 0.5 }}>
              <Typography variant="body2">
                {cv.candidate_experience != null ? `${cv.candidate_experience} years` : '-'}
              </Typography>
              {skills.length > 0 && (
                <Tooltip
                  title={
                    overflowSkills.length > 0 ? (
                      <Box sx={{ maxWidth: 320, maxHeight: 240, overflow: 'auto', py: 0.5 }}>
                        {skills.map((skill) => (
                          <Typography key={skill} variant="caption" display="block" sx={{ lineHeight: 1.6 }}>
                            {skill}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      ''
                    )
                  }
                  disableHoverListener={overflowSkills.length === 0}
                  arrow
                  placement="top"
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ cursor: overflowSkills.length > 0 ? 'help' : 'default' }}
                  >
                    {visibleSkills.join(', ')}
                    {overflowSkills.length > 0 && ` +${overflowSkills.length}`}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        colId: 'status',
        headerName: 'Status',
        field: 'status',
        minWidth: 150,
        maxWidth: 180,
        flex: 0.9,
        cellRenderer: (params: { data?: CVBankRow }) => {
          const cv = params.data;
          if (!cv) return null;
          return (
            <FormControl size="small" fullWidth sx={{ my: 0.5 }} onClick={(e) => e.stopPropagation()}>
              <Select
                value={cv.status}
                onChange={(e) => {
                  onStatusChange(
                    cv.id,
                    e.target.value,
                    cv.custom_tags || [],
                    cv.recruiter_notes || ''
                  );
                }}
                MenuProps={getZoomCompensatedSelectMenuProps()}
                sx={{
                  fontSize: '0.8125rem',
                  color: getStatusSelectColor(cv.status),
                  '.MuiSelect-select': { py: 0.75 },
                }}
              >
                {CV_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        colId: 'tags',
        headerName: 'Tags',
        minWidth: 180,
        flex: 1.3,
        valueGetter: (params) => (params.data?.tags || []).join(', '),
        cellRenderer: (params: { data?: CVBankRow }) => (
          <TagsCell tags={params.data?.tags || []} />
        ),
      },
      {
        colId: 'size',
        headerName: 'Size',
        field: 'file_size',
        minWidth: 90,
        maxWidth: 110,
        flex: 0.6,
        valueFormatter: (params) =>
          typeof params.value === 'number' ? `${(params.value / 1024).toFixed(1)} KB` : '-',
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        width: 160,
        minWidth: 160,
        maxWidth: 180,
        flex: 0,
        sortable: false,
        filter: false,
        suppressSizeToFit: true,
        cellRenderer: (params: { data?: CVBankRow }) => {
          const cv = params.data;
          if (!cv) return null;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.25,
                height: '100%',
              }}
            >
              <Tooltip title="View">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload(cv.id, cv.original_filename);
                  }}
                  aria-label="View"
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Notes">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onComments(cv);
                  }}
                  aria-label="Notes"
                >
                  <CommentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit profile">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (cv.candidate_profile_id) onEditProfile(cv.candidate_profile_id);
                    }}
                    disabled={!cv.candidate_profile_id || profileDialogLoading}
                    aria-label="Edit profile"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(cv.id);
                  }}
                  aria-label="Delete"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [onComments, onDelete, onDownload, onEditProfile, onRatingChange, onStatusChange, profileDialogLoading]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
      suppressMovable: true,
    }),
    []
  );

  return (
    <Paper
      elevation={0}
      sx={{
        height: Math.min(640, Math.max(280, 56 + cvs.length * 64 + 16)),
        mb: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
      className={theme.palette.mode === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
    >
      <AgGridReact<CVBankRow>
        theme="legacy"
        rowData={cvs}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={(params) => String(params.data.id)}
        rowHeight={64}
        pagination={false}
        enableCellTextSelection
        ensureDomOrder
        suppressCellFocus
        animateRows={false}
      />
    </Paper>
  );
};

export default CVBankTableAgGrid;
